/**
 * Operator projection for shipment activity vs carrier tracking.
 * Read-only. Viewing or printing an existing label must not write events.
 */

import { shippingLabelPrintState } from "@/lib/order-documents";

export const VIEW_PRINT_LABEL_MUTATES = false;

export const TRADIVO_TIMELINE_EVENT_CODES = new Set([
  "LABEL_CREATED",
  "TRACKING_RECORDED",
  "DISPATCHED",
  "IN_TRANSIT",
  "DELIVERED",
  "EXCEPTION",
  "CANCELLED",
]);

export type TrackingEventRow = {
  id: string;
  event_code: string | null;
  event_description: string | null;
  location: string | null;
  occurred_at: string | null;
  carrier_code?: string | null;
  event_id?: string | null;
  normalized_status?: string | null;
  provider_status?: string | null;
};

export type ProjectedTimelineEvent = TrackingEventRow & {
  collapsedCount: number;
  collapsedIds: string[];
};

export function viewPrintLabelMutatesOperationalRecords(): boolean {
  return VIEW_PRINT_LABEL_MUTATES;
}

export function isAustraliaPostShipment(s: {
  shipping_provider?: string | null;
  service_name?: string | null;
  carrier_accounts?: { carrier?: string | null } | null;
  carrier_services?: { carrier_code?: string | null; name?: string | null } | null;
}): boolean {
  const code = (
    s.shipping_provider ??
    s.carrier_accounts?.carrier ??
    s.carrier_services?.carrier_code ??
    ""
  )
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  if (code === "auspost" || code === "australia_post" || code === "australia") return true;
  const named = `${s.service_name ?? ""} ${s.carrier_services?.name ?? ""}`.toLowerCase();
  return named.includes("australia post");
}

/** Official public tracker. Encodes the canonical tracking number only. */
export function australiaPostTrackingUrl(trackingNumber: string | null | undefined): string | null {
  const id = (trackingNumber ?? "").trim();
  if (!/^[A-Za-z0-9]{8,40}$/.test(id)) return null;
  return `https://auspost.com.au/mypost/track/details/${encodeURIComponent(id)}`;
}

export function isCarrierPhysicalEvent(ev: TrackingEventRow): boolean {
  const code = (ev.event_code ?? "").trim();
  if (TRADIVO_TIMELINE_EVENT_CODES.has(code)) return false;
  const carrier = (ev.carrier_code ?? "").trim().toLowerCase();
  if (!carrier || carrier === "internal" || carrier === "manual") return false;
  if (!ev.event_id) return false;
  return true;
}

function collapseConsecutive(events: TrackingEventRow[]): ProjectedTimelineEvent[] {
  const out: ProjectedTimelineEvent[] = [];
  for (const ev of events) {
    const prev = out[out.length - 1];
    if (
      prev &&
      (prev.event_code ?? "") === (ev.event_code ?? "") &&
      (prev.event_description ?? "") === (ev.event_description ?? "")
    ) {
      prev.collapsedCount += 1;
      prev.collapsedIds.push(ev.id);
      continue;
    }
    out.push({ ...ev, collapsedCount: 1, collapsedIds: [ev.id] });
  }
  return out;
}

export function projectShipmentTimelines(events: TrackingEventRow[]): {
  tradivo: ProjectedTimelineEvent[];
  carrier: ProjectedTimelineEvent[];
} {
  const tradivo: TrackingEventRow[] = [];
  const carrier: TrackingEventRow[] = [];
  for (const ev of events) {
    if (isCarrierPhysicalEvent(ev)) carrier.push(ev);
    else tradivo.push(ev);
  }
  return {
    tradivo: collapseConsecutive(tradivo),
    carrier: collapseConsecutive(carrier),
  };
}

export function viewPrintLabelAction(shipment: {
  label_url?: string | null;
  carrier_reference?: string | null;
  tracking_number?: string | null;
}): { kind: "open-url"; url: string } | { kind: "unavailable"; message: string } {
  const state = shippingLabelPrintState([shipment]);
  if (state.available) return { kind: "open-url", url: state.url };
  return { kind: "unavailable", message: state.label };
}

export function trackingLinkCreatesEvents(): boolean {
  return false;
}
